import { useEffect } from "react";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import httpCommon from "../../../../http-common";
import assetMakersLogo from "./Asset-Makers-Logo-Brightest.png";

const CreatePDF = ({ open, onClose, data }) => {
  useEffect(() => {
    if (!open || !data || typeof data !== "object") return;

    let cancelled = false;
    const objectUrls = [];

    const createPdf = async () => {
      try {
        const pdfDoc = await PDFDocument.create();

        const PAGE_W = 595.28;
        const PAGE_H = 841.89;
        const MARGIN = 38;
        const CONTENT_W = PAGE_W - MARGIN * 2;
        const FOOTER_Y = 30;
        const CONTENT_BOTTOM = 52;

        const C = {
          ink: rgb(0.12, 0.16, 0.21),
          muted: rgb(0.42, 0.46, 0.50),
          green: rgb(0.08, 0.36, 0.29),
          greenDark: rgb(0.06, 0.27, 0.22),
          greenTint: rgb(0.93, 0.96, 0.94),
          line: rgb(0.87, 0.89, 0.89),
          card: rgb(0.975, 0.98, 0.975),
          white: rgb(1, 1, 1),
          gold: rgb(0.72, 0.52, 0.20),
        };

        const regular = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

        let page;
        let y;
        let pageNumber = 0;

        const clean = (value) =>
          String(value ?? "")
            .replace(/<[^>]*>/g, " ")
            .replace(/&nbsp;/gi, " ")
            .replace(/&amp;/gi, "&")
            .replace(/&lt;/gi, "<")
            .replace(/&gt;/gi, ">")
            .replace(/&#39;|&apos;/gi, "'")
            .replace(/&quot;/gi, '"')
            .replace(/\s+/g, " ")
            .trim();

        const valueOf = (value) => {
          const result = clean(value);
          return result && result.toLowerCase() !== "null" ? result : "";
        };

        const firstValue = (obj, keys) => {
          for (const key of keys) {
            const value = valueOf(obj?.[key]);
            if (value) return value;
          }
          return "";
        };

        const wrap = (text, font, size, maxWidth) => {
          const words = clean(text).split(/\s+/).filter(Boolean);
          const lines = [];
          let current = "";

          for (const word of words) {
            const candidate = current ? `${current} ${word}` : word;

            if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
              current = candidate;
              continue;
            }

            if (current) lines.push(current);

            if (font.widthOfTextAtSize(word, size) <= maxWidth) {
              current = word;
              continue;
            }

            // Split unusually long words so they never run outside the page.
            let chunk = "";
            for (const char of word) {
              const candidateChunk = chunk + char;
              if (
                font.widthOfTextAtSize(candidateChunk, size) > maxWidth &&
                chunk
              ) {
                lines.push(chunk);
                chunk = char;
              } else {
                chunk = candidateChunk;
              }
            }
            current = chunk;
          }

          if (current) lines.push(current);
          return lines.length ? lines : [""];
        };

        // Track pages that actually receive brochure content. This allows
        // removing any accidental blank page before adding the footer.
        const pagesWithContent = new WeakSet();

        const addPage = () => {
          page = pdfDoc.addPage([PAGE_W, PAGE_H]);
          pageNumber += 1;
          y = PAGE_H - MARGIN;

          ["drawText", "drawImage", "drawLine", "drawRectangle", "drawCircle"].forEach(
            (methodName) => {
              const originalMethod = page[methodName].bind(page);
              page[methodName] = (...args) => {
                pagesWithContent.add(page);
                return originalMethod(...args);
              };
            }
          );

          return page;
        };

        const needsPage = (height) => {
          if (y - height < CONTENT_BOTTOM) addPage();
        };

        const drawText = (
          text,
          x,
          atY,
          size = 10,
          font = regular,
          color = C.ink
        ) => {
          page.drawText(clean(text), { x, y: atY, size, font, color });
        };

        const drawTextLines = (
          text,
          x,
          startY,
          maxWidth,
          size = 9.5,
          font = regular,
          color = C.ink,
          leading = size + 4
        ) => {
          const lines = wrap(text, font, size, maxWidth);
          let currentY = startY;

          lines.forEach((line) => {
            needsPage(leading);
            drawText(line, x, currentY, size, font, color);
            currentY -= leading;
            y = currentY;
          });

          return currentY;
        };

        const drawSectionHeading = (heading, subtitle = "") => {
          needsPage(subtitle ? 48 : 35);
          y -= 3;

          drawText(heading.toUpperCase(), MARGIN, y, 12, bold, C.green);
          y -= 9;

          page.drawLine({
            start: { x: MARGIN, y },
            end: { x: PAGE_W - MARGIN, y },
            thickness: 1,
            color: C.line,
          });

          y -= 16;

          if (subtitle) {
            drawText(subtitle, MARGIN, y, 8.5, regular, C.muted);
            y -= 14;
          }
        };

        const drawBrandHeader = async () => {
          // The light/gold logo is now placed on a dark-green background.
          const logoResponse = await fetch(assetMakersLogo);
          if (!logoResponse.ok) {
            throw new Error(`Unable to load Asset Makers logo (${logoResponse.status})`);
          }

          const logoBytes = await logoResponse.arrayBuffer();
          const logoImage = await pdfDoc.embedPng(logoBytes);

          const logoBoxWidth = 190;
          const logoBoxHeight = 43;
          const logoPadding = 7;
          const maxLogoWidth = logoBoxWidth - logoPadding * 2;
          const maxLogoHeight = logoBoxHeight - logoPadding * 2;
          const scale = Math.min(
            maxLogoWidth / logoImage.width,
            maxLogoHeight / logoImage.height
          );
          const logoWidth = logoImage.width * scale;
          const logoHeight = logoImage.height * scale;
          const logoBoxY = y - logoBoxHeight + 3;

          page.drawRectangle({
            x: MARGIN,
            y: logoBoxY,
            width: logoBoxWidth,
            height: logoBoxHeight,
            color: C.greenDark,
          });

          page.drawImage(logoImage, {
            x: MARGIN + (logoBoxWidth - logoWidth) / 2,
            y: logoBoxY + (logoBoxHeight - logoHeight) / 2,
            width: logoWidth,
            height: logoHeight,
          });

          const rightLabel = "PROPERTY BROCHURE";
          const labelWidth = bold.widthOfTextAtSize(rightLabel, 7.5);
          drawText(
            rightLabel,
            PAGE_W - MARGIN - labelWidth,
            y - 10,
            7.5,
            bold,
            C.muted
          );

          y -= logoBoxHeight + 12;

          page.drawLine({
            start: { x: MARGIN, y },
            end: { x: PAGE_W - MARGIN, y },
            thickness: 2,
            color: C.green,
          });

          y -= 22;
        };

        const drawFooter = (currentPage, index, count, featured) => {
          currentPage.drawLine({
            start: { x: MARGIN, y: FOOTER_Y + 12 },
            end: { x: PAGE_W - MARGIN, y: FOOTER_Y + 12 },
            thickness: 0.6,
            color: C.line,
          });

          if (featured) {
            currentPage.drawText("FEATURED PROPERTY", {
              x: MARGIN,
              y: FOOTER_Y,
              size: 7,
              font: bold,
              color: C.green,
            });
          } else {
            currentPage.drawText("ASSET MAKERS  |  PROPERTY BROCHURE", {
              x: MARGIN,
              y: FOOTER_Y,
              size: 7,
              font: regular,
              color: C.muted,
            });
          }

          const pageLabel = `PAGE ${index + 1} OF ${count}`;
          const pageLabelWidth = bold.widthOfTextAtSize(pageLabel, 7);
          currentPage.drawText(pageLabel, {
            x: PAGE_W - MARGIN - pageLabelWidth,
            y: FOOTER_Y,
            size: 7,
            font: bold,
            color: C.muted,
          });
        };

        const imageUrlFor = (attachment) => {
          if (!attachment) return "";

          if (typeof attachment === "string") {
            return attachment;
          }

          if (typeof File !== "undefined" && attachment instanceof File) {
            const url = URL.createObjectURL(attachment);
            objectUrls.push(url);
            return url;
          }

          if (attachment.url) return attachment.url;

          if (attachment.attachment) {
            const relativePath = attachment.attachment
              .replace(/\\/g, "/")
              .replace(/^\/+/, "");
            const baseUrl = httpCommon?.defaults?.baseURL || "";
            return `${baseUrl.replace(/\/$/, "")}/${relativePath}`;
          }

          return "";
        };

        const embedImage = async (url) => {
          const response = await fetch(url);
          if (!response.ok) {
            throw new Error(`Could not fetch image (${response.status})`);
          }

          const bytes = await response.arrayBuffer();
          const mime = response.headers.get("content-type") || "";
          const path = url.toLowerCase().split("?")[0];

          if (mime.includes("png") || path.endsWith(".png")) {
            return pdfDoc.embedPng(bytes);
          }

          if (
            mime.includes("jpeg") ||
            mime.includes("jpg") ||
            path.endsWith(".jpg") ||
            path.endsWith(".jpeg")
          ) {
            return pdfDoc.embedJpg(bytes);
          }

          try {
            return await pdfDoc.embedJpg(bytes);
          } catch {
            return pdfDoc.embedPng(bytes);
          }
        };

        const drawImageContain = (embedded, x, topY, maxW, maxH) => {
          const scale = Math.min(
            maxW / embedded.width,
            maxH / embedded.height
          );
          const width = embedded.width * scale;
          const height = embedded.height * scale;

          page.drawImage(embedded, {
            x: x + (maxW - width) / 2,
            y: topY - height,
            width,
            height,
          });

          return height;
        };

        const drawImageFrame = (x, topY, width, height) => {
          page.drawRectangle({
            x,
            y: topY - height,
            width,
            height,
            borderColor: C.line,
            borderWidth: 0.6,
          });
        };

        const drawHeroImage = async (attachment) => {
          if (!attachment) return;

          try {
            const url = imageUrlFor(attachment);
            if (!url) return;

            const embedded = await embedImage(url);
            const heroHeight = 250;
            needsPage(heroHeight + 18);

            // Soft background frame and centered image, maintaining original ratio.
            page.drawRectangle({
              x: MARGIN,
              y: y - heroHeight,
              width: CONTENT_W,
              height: heroHeight,
              color: C.card,
            });

            drawImageContain(embedded, MARGIN, y, CONTENT_W, heroHeight);
            drawImageFrame(MARGIN, y, CONTENT_W, heroHeight);
            y -= heroHeight + 17;
          } catch (error) {
            console.warn("Unable to embed the main property image:", error);
          }
        };

        const drawHighlightStrip = (items) => {
          const usable = items.filter((item) => item.value);
          if (!usable.length) return;

          const gap = 8;
          const cardW = (CONTENT_W - gap * (usable.length - 1)) / usable.length;
          const cardH = 61;

          needsPage(cardH + 14);

          usable.forEach((item, index) => {
            const x = MARGIN + index * (cardW + gap);

            page.drawRectangle({
              x,
              y: y - cardH,
              width: cardW,
              height: cardH,
              color: C.greenTint,
              borderColor: C.greenTint,
              borderWidth: 0.5,
            });

            drawText(item.label.toUpperCase(), x + 10, y - 16, 7, bold, C.muted);

            const valueLines = wrap(item.value, bold, 11, cardW - 20).slice(0, 2);
            valueLines.forEach((line, lineIndex) => {
              drawText(
                line,
                x + 10,
                y - 34 - lineIndex * 12,
                11,
                bold,
                C.greenDark
              );
            });
          });

          y -= cardH + 17;
        };

        const drawDetailCards = (details) => {
          const visible = details.filter((detail) => detail.value);
          if (!visible.length) return;

          drawSectionHeading("Property Details");

          const gapX = 12;
          const gapY = 8;
          const colW = (CONTENT_W - gapX) / 2;
          const labelSize = 7.5;
          const valueSize = 9.2;
          const leading = 11.5;

          for (let index = 0; index < visible.length; index += 2) {
            const row = visible.slice(index, index + 2);

            const heights = row.map((item) => {
              const lines = wrap(item.value, regular, valueSize, colW - 18);
              return 13 + lines.length * leading + 14;
            });
            const rowH = Math.max(...heights);

            needsPage(rowH + gapY);

            row.forEach((item, column) => {
              const x = MARGIN + column * (colW + gapX);

              page.drawRectangle({
                x,
                y: y - rowH,
                width: colW,
                height: rowH,
                color: C.card,
                borderColor: C.line,
                borderWidth: 0.45,
              });

              drawText(item.label, x + 9, y - 13, labelSize, bold, C.muted);

              const lines = wrap(item.value, regular, valueSize, colW - 18);
              lines.forEach((line, lineIndex) => {
                drawText(
                  line,
                  x + 9,
                  y - 29 - lineIndex * leading,
                  valueSize,
                  regular,
                  C.ink
                );
              });
            });

            y -= rowH + gapY;
          }

          y -= 5;
        };

        const parameterConfig = {
          Apartments: [
            { label: "Flat Super Area", keys: ["ltg_det_pmts_area_dts", "ltg_det_pmts_super_area", "ltg_det_pmts_flat_super_area"], suffix: " Sq Ft" },
            { label: "Rate Per Sq-ft", keys: ["ltg_det_pmts_rate_per_sq"] },
            { label: "Bed Rooms", keys: ["ltg_det_pmts_bed_rom", "ltg_det_pmts_bed_rooms"] },
            { label: "Bath Rooms", keys: ["ltg_det_pmts_bth_rom", "ltg_det_pmts_bath_rooms"] },
            { label: "Status", keys: ["ltg_det_pmts_status"] },
            { label: "Car Parking", keys: ["ltg_det_pmts_car_parking", "ltg_det_pmts_no_of_car_parking"] },
            { label: "Approaching Road Width", keys: ["ltg_det_pmts_approaching_road_width"] },
            { label: "Approved By", keys: ["ltg_det_pmts_approval_authority", "ltg_det_pmts_approved_by"] },
            { label: "Balconies", keys: ["ltg_det_pmts_balconies", "ltg_det_pmts_no_of_balconies"] },
            { label: "Furnishing", keys: ["ltg_det_pmts_furnishing"] },
            { label: "Lifts In The Tower", keys: ["ltg_det_pmts_lifts", "ltg_det_pmts_lifts_in_tower"] },
            { label: "Main Door Facing", keys: ["ltg_det_pmts_main_door_facing"] },
            { label: "Property Flooring", keys: ["ltg_det_pmts_flooring", "ltg_det_pmts_property_flooring"] },
            { label: "Total Floors", keys: ["ltg_det_pmts_total_flrs", "ltg_det_pmts_total_floors"] },
            { label: "Total Project Extent", keys: ["ltg_det_pmts_total_project_extent"] },
            { label: "Total Towers In Society", keys: ["ltg_det_pmts_total_towers", "ltg_det_pmts_total_towers_in_society"] },
            { label: "Year Built", keys: ["ltg_det_pmts_year_built"] },
          ],
          Plots: [
            { label: "Plot Area Details", keys: ["ltg_det_plot_pmts_area_dts"], suffix: " Sq Ft" },
            { label: "Rate Per Sq-ft", keys: ["ltg_det_plot_pmts_rate_per_sq"] },
            { label: "Status", keys: ["ltg_det_plot_pmts_status"] },
            { label: "Total Units", keys: ["ltg_det_plot_pmts_total_units"] },
            { label: "Approaching Road Width", keys: ["ltg_det_plot_pmts_approaching_road_width"] },
            { label: "Approved By", keys: ["ltg_det_plot_pmts_plot_approval_authority"] },
            { label: "Floors Allowed For Construction", keys: ["ltg_det_plot_pmts_floors_allowed_for_construction"] },
            { label: "Is In Gated Community", keys: ["ltg_det_plot_pmts_gated_community"] },
            { label: "Plot Dimensions", keys: ["ltg_det_plot_pmts_plot_dimensions"] },
            { label: "Plot Facing", keys: ["ltg_det_plot_pmts_plot_facing"] },
            { label: "Total Project Extent", keys: ["ltg_det_plot_pmts_total_project_extent"] },
            { label: "Transaction Type", keys: ["ltg_det_plot_pmts_transaction_type"] },
            { label: "Year Built", keys: ["ltg_det_plot_pmts_year_built"] },
          ],
          Villas: [
            { label: "Villa Built Area", keys: ["ltg_det_villa_pmts_built_area", "ltg_det_villa_pmts_builtup_area"], suffix: " Sq Ft" },
            { label: "Rate Per Sq-ft", keys: ["ltg_det_villa_pmts_rate_per_sq"] },
            { label: "Bed Rooms", keys: ["ltg_det_villa_pmts_bed_rom", "ltg_det_villa_pmts_bed_rooms"] },
            { label: "Bath Rooms", keys: ["ltg_det_villa_pmts_bth_rom", "ltg_det_villa_pmts_bath_rooms"] },
            { label: "Status", keys: ["ltg_det_villa_pmts_status"] },
            { label: "Car Parking", keys: ["ltg_det_villa_pmts_car_parking"] },
            { label: "Approaching Road Width", keys: ["ltg_det_villa_pmts_approaching_road_width"] },
            { label: "Approved By", keys: ["ltg_det_villa_pmts_approval_authority", "ltg_det_villa_pmts_approved_by"] },
            { label: "Balconies", keys: ["ltg_det_villa_pmts_balconies"] },
            { label: "Duplex", keys: ["ltg_det_villa_pmts_duplex"] },
            { label: "Furnishing", keys: ["ltg_det_villa_pmts_furnishing"] },
            { label: "Is In Gated Community", keys: ["ltg_det_villa_pmts_gated_community"] },
            { label: "Main Door Facing", keys: ["ltg_det_villa_pmts_main_door_facing"] },
            { label: "Plot Area", keys: ["ltg_det_villa_pmts_plot_area"], suffix: " Sq Ft" },
            { label: "Plot Dimensions", keys: ["ltg_det_villa_pmts_plot_dimensions"] },
            { label: "Plot Facing", keys: ["ltg_det_villa_pmts_plot_facing"] },
            { label: "Property Flooring", keys: ["ltg_det_villa_pmts_flooring"] },
            { label: "Total Floors", keys: ["ltg_det_villa_pmts_total_flrs"] },
            { label: "Total Project Extent", keys: ["ltg_det_villa_pmts_total_project_extent"] },
            { label: "Total Units", keys: ["ltg_det_villa_pmts_total_units"] },
            { label: "Year Built", keys: ["ltg_det_villa_pmts_year_built"] },
          ],
        };

        const buildParameters = (listing) => {
          const type = valueOf(listing?.ltg_type);
          const fields = parameterConfig[type] || [];

          return fields
            .map((field) => {
              const value = firstValue(listing, field.keys);
              if (!value) return null;

              return {
                label: field.label,
                value: `${value}${field.suffix || ""}`,
              };
            })
            .filter(Boolean);
        };

        const drawParameterCards = (parameters) => {
          if (!parameters.length) return;

          drawSectionHeading("Property Parameters");

          const gapX = 10;
          const gapY = 8;
          const colW = (CONTENT_W - gapX) / 2;
          const labelSize = 7.3;
          const valueSize = 9.5;
          const leading = 12;

          for (let index = 0; index < parameters.length; index += 2) {
            const row = parameters.slice(index, index + 2);

            const heights = row.map((item) => {
              const valueLines = wrap(item.value, bold, valueSize, colW - 18);
              const labelLines = wrap(item.label, regular, labelSize, colW - 18);
              return 12 + labelLines.length * 9 + 4 + valueLines.length * leading + 12;
            });

            const rowH = Math.max(...heights);
            needsPage(rowH + gapY);

            row.forEach((item, column) => {
              const x = MARGIN + column * (colW + gapX);

              page.drawRectangle({
                x,
                y: y - rowH,
                width: colW,
                height: rowH,
                color: C.greenTint,
                borderColor: C.greenTint,
                borderWidth: 0.4,
              });

              const labelLines = wrap(item.label, regular, labelSize, colW - 18);
              let textY = y - 13;

              labelLines.forEach((line) => {
                drawText(line, x + 9, textY, labelSize, regular, C.muted);
                textY -= 9;
              });

              textY -= 3;

              const valueLines = wrap(item.value, bold, valueSize, colW - 18);
              valueLines.forEach((line) => {
                drawText(line, x + 9, textY, valueSize, bold, C.ink);
                textY -= leading;
              });
            });

            y -= rowH + gapY;
          }

          y -= 5;
        };

        const drawAmenities = (listing) => {
          const amenityString = firstValue(listing, [
            "ltg_det_plot_amenities",
            "ltg_det_pmts_amenities",
            "ltg_det_villa_pmts_amenities",
            "ltg_det_amenities",
          ]);

          if (!amenityString) return;

          const amenities = amenityString
            .split(",")
            .map((item) => clean(item))
            .filter(Boolean);

          if (!amenities.length) return;

          drawSectionHeading("Amenities");

          const gapX = 14;
          const colW = (CONTENT_W - gapX) / 2;
          const fontSize = 8.5;
          const leading = 12;
          const bulletSize = 2.2;

          for (let index = 0; index < amenities.length; index += 2) {
            const row = amenities.slice(index, index + 2);
            const rowLines = row.map((item) =>
              wrap(item, regular, fontSize, colW - 17)
            );
            const rowH = Math.max(...rowLines.map((lines) => lines.length)) * leading + 9;

            needsPage(rowH);

            row.forEach((item, column) => {
              const x = MARGIN + column * (colW + gapX);
              const lines = rowLines[column];

              page.drawCircle({
                x: x + 4,
                y: y - 1,
                size: bulletSize,
                color: C.green,
              });

              lines.forEach((line, lineIndex) => {
                drawText(
                  line,
                  x + 13,
                  y - lineIndex * leading,
                  fontSize,
                  regular,
                  C.ink
                );
              });
            });

            y -= rowH;
          }

          y -= 7;
        };

        const drawDescription = (listing) => {
          const description = firstValue(listing, [
            "ltg_det_plot_desc",
            "ltg_det_desc",
            "ltg_description",
          ]);

          if (!description) return;

          drawSectionHeading("About This Property");
          y = drawTextLines(
            description,
            MARGIN,
            y,
            CONTENT_W,
            9,
            regular,
            C.ink,
            13
          );
          y -= 8;
        };

        const drawGallery = async (attachments) => {
          if (!attachments?.length) return;

          drawSectionHeading("Property Gallery");

          const gap = 10;
          const colW = (CONTENT_W - gap) / 2;
          const frameH = 150;

          for (let index = 0; index < attachments.length; index += 2) {
            needsPage(frameH + 14);
            const row = attachments.slice(index, index + 2);

            for (let column = 0; column < row.length; column++) {
              const attachment = row[column];

              try {
                const url = imageUrlFor(attachment);
                if (!url) continue;

                const embedded = await embedImage(url);
                const x = MARGIN + column * (colW + gap);

                page.drawRectangle({
                  x,
                  y: y - frameH,
                  width: colW,
                  height: frameH,
                  color: C.card,
                  borderColor: C.line,
                  borderWidth: 0.5,
                });

                drawImageContain(embedded, x + 3, y - 3, colW - 6, frameH - 6);
              } catch (error) {
                console.warn("Unable to add gallery image:", error);
              }
            }

            y -= frameH + 14;
          }
        };

        const drawBrochureAttachments = async (brochures) => {
          for (const brochure of brochures) {
            try {
              const url = imageUrlFor(brochure);
              if (!url) continue;

              const response = await fetch(url);
              if (!response.ok) {
                throw new Error(`Brochure fetch failed (${response.status})`);
              }

              const bytes = await response.arrayBuffer();
              const sourcePdf = await PDFDocument.load(bytes);
              const copied = await pdfDoc.copyPages(
                sourcePdf,
                sourcePdf.getPageIndices()
              );

              copied.forEach((copiedPage) => {
                pdfDoc.addPage(copiedPage);
                pagesWithContent.add(copiedPage);
              });
            } catch (error) {
              console.warn("Unable to append brochure PDF:", error);
            }
          }
        };

        // ------------------ Compose brochure ------------------
        addPage();
        await drawBrandHeader();

        const title = valueOf(data.ltg_title) || "Property Listing";
        const propertyType = valueOf(data.ltg_type) || "Property";
        const location = firstValue(data, [
          "ltg_det_plot_address",
          "ltg_det_plot_location",
          "ltg_det_address",
        ]);

        const featured =
          data.ltg_mark_as_featured === "1" ||
          data.ltg_mark_as_featured === "true";

        const titleLines = wrap(title, bold, 19, CONTENT_W);
        titleLines.forEach((line) => {
          needsPage(26);
          drawText(line, MARGIN, y, 19, bold, C.ink);
          y -= 24;
        });

        if (location) {
          drawText(`LOCATION: ${location}`, MARGIN, y, 8.5, regular, C.muted);
          y -= 17;
        }

        if (featured) {
          const tag = "FEATURED PROPERTY";
          const tagW = bold.widthOfTextAtSize(tag, 7) + 18;

          page.drawRectangle({
            x: MARGIN,
            y: y - 13,
            width: tagW,
            height: 19,
            color: C.greenTint,
          });

          drawText(tag, MARGIN + 9, y - 7, 7, bold, C.green);
          y -= 25;
        } else {
          y -= 5;
        }

        const attachments = Array.isArray(data.attachments)
          ? data.attachments
          : [];

        const mainImage = attachments.find((item) => item.type === "Main");
        const gallery = attachments.filter(
          (item) => item.type !== "Main" && item.type !== "Brochure"
        );
        const brochures = attachments.filter((item) => item.type === "Brochure");

        await drawHeroImage(mainImage);

        const salePrice = firstValue(data, [
          "ltg_det_plot_sale_price",
          "ltg_det_sale_price",
          "ltg_det_pmts_sale_price",
          "ltg_det_villa_sale_price",
        ]);

        const plotArea = firstValue(data, [
          "ltg_det_plot_pmts_area_dts",
          "ltg_det_pmts_area_dts",
          "ltg_det_villa_pmts_plot_area",
        ]);

        const rate = firstValue(data, [
          "ltg_det_plot_pmts_rate_per_sq",
          "ltg_det_pmts_rate_per_sq",
          "ltg_det_villa_pmts_rate_per_sq",
        ]);

        const status = firstValue(data, [
          "ltg_det_plot_pmts_status",
          "ltg_det_pmts_status",
          "ltg_det_villa_pmts_status",
        ]);

        drawHighlightStrip([
          { label: "Sale Price", value: salePrice ? `INR ${salePrice}` : "" },
          { label: propertyType === "Plots" ? "Plot Area" : "Property Type", value: plotArea ? `${plotArea} Sq Ft` : propertyType },
          { label: "Status", value: status },
        ]);

        const details = [
          { label: "Property Owner", value: valueOf(data.ltg_owner) },
          { label: "Property Type", value: propertyType },
          { label: "Category", value: valueOf(data.ltg_categories) },
          { label: "Region", value: valueOf(data.ltg_regions) },
          { label: "Rate Per Sq-ft", value: rate },
          { label: "Postal Code", value: valueOf(data.ltg_det_plot_postal_code) },
          {
            label: "Property Address",
            value: firstValue(data, [
              "ltg_det_plot_address",
              "ltg_det_plot_location",
              "ltg_det_address",
            ]),
          },
        ];

        drawDetailCards(details);
        drawDescription(data);
        drawParameterCards(buildParameters(data));
        drawAmenities(data);

        if (gallery.length) {
          await drawGallery(gallery);
        }

        await drawBrochureAttachments(brochures);

        if (cancelled) return;

        // Remove pages created by a page-break check that never received
        // any content. This avoids an empty trailing/second page.
        let pages = pdfDoc.getPages();
        for (let index = pages.length - 1; index >= 0; index -= 1) {
          if (!pagesWithContent.has(pages[index])) {
            pdfDoc.removePage(index);
          }
        }

        pages = pdfDoc.getPages();
        pages.forEach((currentPage, index) => {
          drawFooter(currentPage, index, pages.length, featured);
        });

        const bytes = await pdfDoc.save();
        if (cancelled) return;

        const blob = new Blob([bytes], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        objectUrls.push(blobUrl);

        const anchor = document.createElement("a");
        anchor.href = blobUrl;
        anchor.download = `${title.replace(/[\\/:*?"<>|]/g, "-") || "property-brochure"}.pdf`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();

        window.setTimeout(() => {
          objectUrls.forEach((url) => URL.revokeObjectURL(url));
        }, 1500);
      } catch (error) {
        console.error("Error creating property PDF:", error);
      }
    };

    createPdf();

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [open, data]);

  return null;
};

export default CreatePDF;
